import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Flex, theme } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import { INTEGRATIONS_ROUTE, SEARCH_PERFORMANCE_RECONNECT_ROUTE } from '../SearchPerformanceRoutes';
import useTrackSearchPerformanceEvent from '../useTrackSearchPerformanceEvent';

const STRINGS = {
  message: 'Reconnect needed',
  description:
    'Google stopped accepting the connection, so there is nothing to list. Reconnect to manage your synced domains.',
  lostDescription: (propertyUrls: string[]) =>
    `Google Search Console no longer shares ${propertyUrls.join(', ')} with the connected account, and no other synced property can list your domains. Add ${
      propertyUrls.length === 1 ? 'it' : 'them'
    } back in Search Console and reconnect to resume syncing, or disconnect the account from Integrations.`,
  reconnect: 'Reconnect',
  back: 'Back to Integrations',
};

type Props = {
  lostUrls?: string[];
};

function GscSettingsReconnectNeeded({ lostUrls = [] }: Props) {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const trackEvent = useTrackSearchPerformanceEvent();

  // `?reconnect=1` — without it a still-live source lands on the sync step.
  const handleReconnect = () => {
    trackEvent('Search Performance: Integration Reconnect Clicked');
    navigate(SEARCH_PERFORMANCE_RECONNECT_ROUTE);
  };

  return (
    <Card styles={{ body: { paddingTop: token.padding } }}>
      <Flex vertical gap={token.margin}>
        <Alert
          type="warning"
          showIcon
          message={STRINGS.message}
          description={lostUrls.length > 0 ? STRINGS.lostDescription(lostUrls) : STRINGS.description}
        />

        <Flex justify="space-between" align="center">
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            style={{ paddingLeft: 0 }}
            onClick={() => navigate(INTEGRATIONS_ROUTE)}
          >
            {STRINGS.back}
          </Button>
          <Button type="primary" size="large" onClick={handleReconnect}>
            {STRINGS.reconnect}
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
}

export default GscSettingsReconnectNeeded;
